// src/types/production.ts
//
// Production module data model. Phase 1 shipped Items + Recipes. Phase 2
// adds Products (BOM), Planning, and the storage-provider abstraction so
// the Items/Recipes/Products collections can be sourced from localStorage,
// Advantage, or DuckDB without the pages caring which.

// ─── Items ────────────────────────────────────────────────────────────────

/**
 * Per-100g nutrition facts. Optional on items — only items used as recipe
 * components in label generation need it. Phase 2D nutrition label engine
 * reads these and scales by recipe yield → per-serving values.
 */
export interface ItemNutrition {
  kcal?: number;
  fat?: number;
  satfat?: number;
  transfat?: number;
  cholesterol?: number;  // mg
  sodium?: number;       // mg
  carbs?: number;
  fiber?: number;
  sugar?: number;
  addedSugar?: number;
  protein?: number;
  vitD?: number;         // mcg
  calcium?: number;      // mg
  iron?: number;         // mg
  potassium?: number;    // mg
}

export interface ProductionItem {
  id: string;
  name: string;
  description?: string;
  /** Default unit of measure (e.g. 'lb', 'kg', 'oz', 'g', 'ea'). */
  unit: string;
  /** Cost per unit of `unit` above. */
  costPerUnit: number;
  category?: string;
  /** Phase 2: on-hand quantity (in the item's default unit). Used by Planning shortage view. */
  onHand?: number;
  /** Phase 2D: nutrition facts per 100g. */
  nutrition?: ItemNutrition;
  /** Phase 2D: allergens declared on this item (e.g. ['Milk', 'Tree Nuts']). */
  allergens?: string[];
  createdAt: string;
  updatedAt: string;
}

// ─── Recipes ──────────────────────────────────────────────────────────────

export interface RecipeComponent {
  itemId: string;
  qty: number;
  unit: string;
  notes?: string;
}

export interface ProductionRecipe {
  id: string;
  name: string;
  description?: string;
  category?: string;
  yieldQty: number;
  yieldUnit: string;
  components: RecipeComponent[];
  sellPrice?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Products (BOM = finished products) ───────────────────────────────────

/** A packaging line on a Product — references an Item (jar, lid, label, etc.). */
export interface ProductPackagingLine {
  itemId: string;
  qty: number;
  unit: string;
}

export interface ProductionProduct {
  id: string;
  name: string;
  /** Customer-facing SKU (optional). */
  sku?: string;
  description?: string;
  category?: string;
  /** Which recipe fills this product. */
  recipeId: string;
  /** How much of the recipe's yield goes into one unit of this product (in recipe's yield unit). */
  unitsPerBatch: number;
  /** Packaging items (jar, label, etc.). Cost rolls into product cost. */
  packaging: ProductPackagingLine[];
  /** Optional sell price per unit for margin reporting. */
  sellPrice?: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Planning ─────────────────────────────────────────────────────────────

/** A single line of forecast — "we expect to sell N units of product X by date Y". */
export interface ForecastLine {
  id: string;
  productId: string;
  qty: number;
  /** Optional target date — Phase 2C-lite ignores date math, treats all rows as a single horizon. */
  targetDate?: string;
  notes?: string;
}

/** Computed required-batches for one recipe, derived from forecast. */
export interface RequiredBatch {
  recipeId: string;
  recipeName: string;
  batchesNeeded: number;
  /** Sum of yields produced × unitsPerBatch — units of recipe yield. */
  totalRecipeYield: number;
}

/** Computed required-items + shortage for one item. */
export interface RequiredItem {
  itemId: string;
  itemName: string;
  unit: string;
  qtyNeeded: number;
  onHand: number;
  shortage: number; // max(0, qtyNeeded - onHand)
  costToProcure: number; // shortage * costPerUnit
}

// ─── Storage backend ──────────────────────────────────────────────────────

export type ProductionStorageBackend = 'local' | 'advantage' | 'duckdb';

/**
 * Per-collection backend assignment. Each collection can independently come
 * from a different source — e.g. Items from Advantage (read-only mirror of
 * Invent table), Recipes from local (user-authored), Products from local.
 *
 * Phase 2D-lite: only `local` is functional; advantage + duckdb are stubs.
 */
export interface ProductionStorageConfig {
  items:    ProductionStorageBackend;
  recipes:  ProductionStorageBackend;
  products: ProductionStorageBackend;
}

export const DEFAULT_STORAGE_CONFIG: ProductionStorageConfig = {
  items:    'local',
  recipes:  'local',
  products: 'local',
};

// ─── Export bundle (for Settings → Export / Import) ───────────────────────

export interface ProductionExport {
  version: 1;
  tenant: string;
  exportedAt: string;
  items: ProductionItem[];
  recipes: ProductionRecipe[];
  products: ProductionProduct[];
  forecast: ForecastLine[];
}
